import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import type { TenantMember } from '@/types'
import { Trash2, X } from 'lucide-react'

interface DeleteMemberDialogProps {
  member: TenantMember | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onConfirm: () => void
  isDeleting: boolean
}

export function DeleteMemberDialog({
  member,
  open,
  onOpenChange,
  onConfirm,
  isDeleting,
}: DeleteMemberDialogProps) {
  const displayName = member?.displayName || member?.invitedEmail || 'this user'

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!isDeleting) onOpenChange(o) }}>
      <DialogContent
        className="max-w-[640px] rounded-2xl p-8 gap-6 border-none shadow-elevated"
        hideCloseButton
      >
        <DialogHeader className="flex-row items-center justify-between space-y-0">
          <DialogTitle className="text-2xl font-bold leading-[1.3] tracking-[0.015em] text-foreground">Delete User?</DialogTitle>
          <DialogClose
            className="rounded-sm opacity-70 transition-opacity hover:opacity-100 focus:outline-none"
            disabled={isDeleting}
          >
            <X className="h-6 w-6" />
            <span className="sr-only">Close</span>
          </DialogClose>
        </DialogHeader>
        <DialogDescription className="text-sm font-normal leading-[1.6] tracking-[0.015em] text-foreground">
          Are you sure you want to delete the user "<span className="font-bold">{displayName}</span>"? This action
          cannot be undone and the user will be permanently removed from the list.
        </DialogDescription>
        <DialogFooter>
          <Button
            type="button"
            variant="ghost"
            onClick={() => onOpenChange(false)}
            disabled={isDeleting}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold"
            data-testid="cancel-delete-member"
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="destructive"
            onClick={onConfirm}
            disabled={isDeleting}
            className="h-10 min-w-10 rounded-lg py-2.5 px-4 gap-2.5 font-bold [&_svg]:size-6"
            data-testid="confirm-delete-member"
          >
            <Trash2 className="h-6 w-6" />
            {isDeleting ? 'Deleting...' : 'Delete User'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}
